<script setup>
    import { ref, computed, watch, defineEmits, defineProps, onMounted, onUnmounted } from 'vue';

    const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: 'Enter phone number'
    },
    label: {
        type: String,
        default: 'Phone Number'
    },
    inputId: {
        type: String,
        default: 'phone'
    },
    inputClass: {
        type: String,
        default: ''
    },
    defaultCountry: {
        type: String,
        default: 'US'
    }
    });

    const emit = defineEmits(['update:modelValue', 'country-changed', 'validate']);

    // Country data - common countries with their dial codes and emojis
    const countries = ref([
        { iso2: 'US', dialCode: '1', name: 'United States', emoji: '🇺🇸', format: '(xxx) xxx-xxxx' },
        // { iso2: 'CA', dialCode: '1', name: 'Canada', emoji: '🇨🇦', format: '(xxx) xxx-xxxx' },
        { iso2: 'GB', dialCode: '44', name: 'United Kingdom', emoji: '🇬🇧', format: 'xxxx xxxxxx' },
        { iso2: 'AU', dialCode: '61', name: 'Australia', emoji: '🇦🇺', format: 'xxxx xxx xxx' },
        { iso2: 'DE', dialCode: '49', name: 'Germany', emoji: '🇩🇪', format: 'xxx xxxxxxxx' },
        { iso2: 'FR', dialCode: '33', name: 'France', emoji: '🇫🇷', format: 'x xx xx xx xx' },
        { iso2: 'JP', dialCode: '81', name: 'Japan', emoji: '🇯🇵', format: 'xx-xxxx-xxxx' },
        { iso2: 'CN', dialCode: '86', name: 'China', emoji: '🇨🇳', format: 'xxx xxxx xxxx' },
        { iso2: 'IN', dialCode: '91', name: 'India', emoji: '🇮🇳', format: 'xxxxx xxxxx' },
        { iso2: 'BR', dialCode: '55', name: 'Brazil', emoji: '🇧🇷', format: '(xx) xxxxx-xxxx' },
        { iso2: 'MX', dialCode: '52', name: 'Mexico', emoji: '🇲🇽', format: 'xxx xxx xxxx' },
        { iso2: 'ES', dialCode: '34', name: 'Spain', emoji: '🇪🇸', format: 'xxx xx xx xx' },
        { iso2: 'IT', dialCode: '39', name: 'Italy', emoji: '🇮🇹', format: 'xxx xxx xxxx' },
        { iso2: 'NL', dialCode: '31', name: 'Netherlands', emoji: '🇳🇱', format: 'xx xxxxxxxx' },
        { iso2: 'RU', dialCode: '7', name: 'Russia', emoji: '🇷🇺', format: 'xxx xxx-xx-xx' },
        { iso2: 'KR', dialCode: '82', name: 'South Korea', emoji: '🇰🇷', format: 'xxx-xxxx-xxxx' },
        { iso2: 'SG', dialCode: '65', name: 'Singapore', emoji: '🇸🇬', format: 'xxxx xxxx' },
        { iso2: 'ZA', dialCode: '27', name: 'South Africa', emoji: '🇿🇦', format: 'xxx xxx xxxx' },
        { iso2: 'AE', dialCode: '971', name: 'UAE', emoji: '🇦🇪', format: 'xx xxx xxxx' },
        { iso2: 'SA', dialCode: '966', name: 'Saudi Arabia', emoji: '🇸🇦', format: 'xx xxx xxxx' }
    ]);

    const selectedCountryCode = ref(props.defaultCountry || 'US');
    const phoneNumberOnly = ref('');
    const isValid = ref(true);
    const isDropdownOpen = ref(false);

    const availableCountries = computed(() => {
        return [...countries.value].sort((a, b) => a.name.localeCompare(b.name));
    });

    const currentCountry = computed(() => {
        return countries.value.find(country => country.iso2 === selectedCountryCode.value) || countries.value[0];
    });

    const currentPlaceholder = computed(() => {
        return currentCountry.value.format.replace(/x/g, '0');
    });

    const fullPhoneNumber = computed(() => {
    if (!phoneNumberOnly.value) return '';
        return `+${currentCountry.value.dialCode} ${phoneNumberOnly.value}`;
    });

    // Initialize phone number from modelValue
    const initializePhoneNumber = () => {
        if (props.modelValue) {
            const phoneStr = String(props.modelValue).trim();
            
            // Case 1: Formatted number with + and space (e.g., "+1 9708895470")
            const formattedMatch = phoneStr.match(/^\+(\d{1,4})\s*(.*)$/);
            if (formattedMatch) {
                const dialCode = formattedMatch[1];
                const number = formattedMatch[2];
                
                // Find country by dial code
                const country = countries.value.find(c => c.dialCode === dialCode);
                if (country) {
                    selectedCountryCode.value = country.iso2;
                    phoneNumberOnly.value = formatPhoneNumber(number);
                    return;
                }
            }
            
            // Case 2: Raw number without formatting (e.g., "19708895470")
            // Try to match against known country codes
            const digits = phoneStr.replace(/\D/g, '');
            if (digits.length >= 7) {
                // Sort countries by dial code length (longest first) to match correctly
                const sortedCountries = [...countries.value].sort((a, b) => b.dialCode.length - a.dialCode.length);
                
                for (const country of sortedCountries) {
                    if (digits.startsWith(country.dialCode)) {
                        selectedCountryCode.value = country.iso2;
                        // Extract the number part (remove country code)
                        const numberPart = digits.substring(country.dialCode.length);
                        phoneNumberOnly.value = formatPhoneNumber(numberPart);
                        return;
                    }
                }
            }
            
            // Case 3: Fallback - couldn't determine country code, use as-is with default country
            phoneNumberOnly.value = formatPhoneNumber(phoneStr);
        }
    };

    // Validate phone number (basic validation)
    const validatePhoneNumber = () => {
        if (!phoneNumberOnly.value) {
            isValid.value = true;
            return;
        }
    
        // Basic validation - just check if it contains only numbers, spaces, dashes, parentheses
        const cleanNumber = phoneNumberOnly.value.replace(/[\s\-()]/g, '');
        isValid.value = /^\d{7,15}$/.test(cleanNumber);
        
        emit('validate', {
            number: fullPhoneNumber.value,
            valid: isValid.value,
            country: currentCountry.value
        });
    };

    const formatPhoneNumber = (value) => {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');
        
        // Get the current country's format
        const format = currentCountry.value.format;
        
        // Apply formatting based on country format
        let formatted = '';
        let digitIndex = 0;
        
        for (let i = 0; i < format.length && digitIndex < digits.length; i++) {
            const formatChar = format[i];
            if (formatChar === 'x') {
                // Replace 'x' with actual digit
                formatted += digits[digitIndex];
                digitIndex++;
            } else {
                // Keep format character (parentheses, spaces, dashes)
                formatted += formatChar;
            }
        }
        
        // If there are remaining digits, append them
        if (digitIndex < digits.length) {
            formatted += digits.slice(digitIndex);
        }
        
        return formatted;
    };

    const onPhoneInput = (event) => {
        const rawValue = event.target.value;
        
        // Format the input
        const formattedValue = formatPhoneNumber(rawValue);
        
        // Update the model with formatted value
        phoneNumberOnly.value = formattedValue;
        
        // Set cursor position to maintain typing flow
        const cursorPosition = event.target.selectionStart;
        const oldLength = rawValue.length;
        const newLength = formattedValue.length;
        
        // Adjust cursor position after formatting
        setTimeout(() => {
            if (event.target) {
                const adjustment = newLength - oldLength;
                event.target.setSelectionRange(cursorPosition + adjustment, cursorPosition + adjustment);
            }
        }, 0);
        
        validatePhoneNumber();
        if (isValid.value) {
            emit('update:modelValue', fullPhoneNumber.value.replace(/\D/g, ''));
        }
    };

    const toggleDropdown = () => {
        isDropdownOpen.value = !isDropdownOpen.value;
    };

    const selectCountry = (countryCode) => {
        selectedCountryCode.value = countryCode;
        isDropdownOpen.value = false;
        onCountryCodeChange();
    };

    const onCountryCodeChange = () => {
        // Reformat the existing number with the new country's format
        if (phoneNumberOnly.value) {
            phoneNumberOnly.value = formatPhoneNumber(phoneNumberOnly.value);
        }
        
        validatePhoneNumber();
        emit('country-changed', currentCountry.value);
        emit('update:modelValue', fullPhoneNumber.value);
    };

    // Watch for external changes to modelValue
    watch(() => props.modelValue, (newValue) => {
        if (newValue !== fullPhoneNumber.value) {
            initializePhoneNumber();
        }
    });

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
        if (!event.target.closest('.relative')) {
            isDropdownOpen.value = false;
        }
    };

    // Initialize on mount
    onMounted(() => {
        initializePhoneNumber();
        validatePhoneNumber();
        document.addEventListener('click', handleClickOutside);
    });

    // Cleanup on unmount
    onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside);
    });
</script>

<style scoped>
    /* Custom styles for the international phone input */
    .flex input:focus,
    .flex select:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 1px #3b82f6;
    }

    /* Ensure the country selector and input have matching heights */
    .flex > div {
        height: auto;
        min-height: 3rem;
    }

    /* Style the select dropdown */
    select {
        appearance: none;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
        background-position: right 0.5rem center;
        background-repeat: no-repeat;
        background-size: 1.5em 1.5em;
        padding-right: 2.5rem;
    }

    /* select:disabled {
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%9ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
    } */
</style>

<template>
    <div class="relative">
        <div class="flex">
            <!-- Country selector and code display -->
                         <div
                 class="relative flex items-center border border-mc-steel dark:border-line-on-dark border-r-0 rounded-l-md px-3 py-2"
                 :class="disabled ? 'bg-mesh-neutral dark:bg-white/[0.03] cursor-default' : 'bg-mc-cloud dark:bg-white/[0.05]'"
             >
                             <!-- Country dropdown button -->
                             <button
                    type="button"
                    @click="toggleDropdown"
                    :disabled="disabled"
                    class="bg-transparent border-none outline-none text-sm font-medium text-mc-ink dark:text-on-dark flex items-center space-x-1 focus:outline-none"
                    :class="disabled ? 'cursor-default' : 'cursor-pointer'"
                >
                       <span>{{ currentCountry.emoji }} +{{ currentCountry.dialCode }}</span>
                       <svg
                           v-if="!disabled"
                           class="w-4 h-4 ml-1"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                       >
                           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                       </svg>
                   </button>

                <!-- Custom dropdown menu -->
                <div
                    v-if="isDropdownOpen && !disabled"
                    class="absolute top-full left-0 mt-1 bg-mc-white dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-md shadow-lg z-50 max-h-60 overflow-y-auto w-80"
                >
                    <div
                        v-for="country in availableCountries"
                        :key="country.iso2"
                        @click="selectCountry(country.iso2)"
                        class="px-4 py-3 text-sm cursor-pointer hover:bg-mc-cloud dark:hover:bg-white/10 flex items-center justify-between whitespace-nowrap"
                        :class="selectedCountryCode === country.iso2 ? 'bg-mc-blue/10 text-mc-blue' : 'text-mc-ink dark:text-on-dark'"
                        >
                        <span class="flex items-center space-x-3">
                            <span class="text-lg">{{ country.emoji }}</span>
                            <span class="font-medium">{{ country.name }}</span>
                        </span>
                        <span class="text-ink-muted dark:text-on-dark-muted font-medium ml-4">+{{ country.dialCode }}</span>
                    </div>
                </div>
            </div>

            <!-- Phone number input -->
            <div class="relative flex-1">
                <input
                    v-model="phoneNumberOnly"
                    @input="onPhoneInput"
                    :id="inputId"
                    :disabled="disabled"
                    :placeholder="currentPlaceholder"
                    class="w-full rounded-r-md pt-6 pb-2 px-3 border border-mc-steel dark:border-line-on-dark focus:border-mc-blue focus:outline-none text-mc-ink dark:text-on-dark placeholder:text-ink-muted dark:placeholder:text-on-dark-muted transition-colors"
                    :class="disabled ? 'bg-mesh-neutral dark:bg-white/[0.03]' : 'bg-mc-cloud dark:bg-white/[0.05]'"
                    type="tel"
                    maxlength="20"
                />
                <label
                    v-if="label"
                    :for="inputId"
                    class="absolute left-3 top-1 text-xs text-ink-muted dark:text-on-dark-muted font-medium pointer-events-none transition-all duration-200 z-10"
                >
                    {{ label }}
                </label>
            </div>
        </div>
        
        <div v-if="!isValid && phoneNumberOnly" class="mt-1 text-sm text-red-600">
            Please enter a valid phone number
        </div>
    </div>
</template>
  